import { useEffect, useRef, useState } from 'react';
import type { SaleSharePayload } from '@klothiq/domain';
import { fetchSaleShare } from '@/lib/salesOverview';
import { createSaleReceiptFile } from '@/lib/saleReceiptShare';

/** Prepare/verify separately so the final native Share call retains the user's tap. */
export function useSaleReceiptShare({
  saleId,
  payload,
  includeAddress,
  active,
  onChanged,
}: {
  saleId: string;
  payload?: SaleSharePayload;
  includeAddress: boolean;
  active: boolean;
  onChanged: () => Promise<unknown>;
}) {
  const [file, setFile] = useState<File>(),
    [url, setUrl] = useState(''),
    [pending, setPending] = useState(false),
    [error, setError] = useState(''),
    [preparedAt, setPreparedAt] = useState<number>(),
    [expired, setExpired] = useState(false);
  const generation = useRef(0);
  useEffect(() => {
    generation.current++;
    setFile(undefined);
    setPreparedAt(undefined);
    setError('');
    setPending(false);
    setExpired(false);
  }, [payload, includeAddress, active, saleId]);
  useEffect(
    () => () => {
      generation.current++;
    },
    [],
  );
  useEffect(() => {
    if (!file) {
      setUrl('');
      return;
    }
    const objectUrl = URL.createObjectURL(file);
    setUrl(objectUrl);
    return () => URL.revokeObjectURL(objectUrl);
  }, [file]);
  useEffect(() => {
    if (!preparedAt) return;
    const timer = window.setTimeout(() => setExpired(true), 60000);
    return () => window.clearTimeout(timer);
  }, [preparedAt]);
  async function prepare() {
    if (!payload || !active || pending) return;
    const currentGeneration = generation.current;
    setPending(true);
    setError('');
    setFile(undefined);
    setPreparedAt(undefined);
    setExpired(false);
    try {
      const fresh = await fetchSaleShare(saleId);
      if (generation.current !== currentGeneration) return;
      if (JSON.stringify(fresh) !== JSON.stringify(payload)) {
        await onChanged();
        throw new Error(
          'The sale changed. Review the refreshed details and prepare the PDF again.',
        );
      }
      const result = await createSaleReceiptFile(fresh, includeAddress);
      if (generation.current !== currentGeneration) return;
      const verified = await fetchSaleShare(saleId);
      if (JSON.stringify(verified) !== JSON.stringify(fresh)) {
        await onChanged();
        throw new Error(
          'The sale changed while preparing the receipt. Review it and prepare the PDF again.',
        );
      }
      if (generation.current !== currentGeneration) return;
      setFile(result);
      setPreparedAt(Date.now());
    } catch (e) {
      if (generation.current === currentGeneration)
        setError(
          e instanceof Error ? e.message : 'The receipt could not be prepared. Please try again.',
        );
    } finally {
      if (generation.current === currentGeneration) setPending(false);
    }
  }
  return { file, url, pending, error, preparedAt, expired, prepare };
}
